import type { SessionConfig } from '../types';

const DEFAULT_SESSION_KEY = 'rw_session_id';
const DEFAULT_TTL_MINUTES = 30;

function getStorage(kind?: 'local' | 'session'): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function generateSessionId() {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `sess_${crypto.randomUUID()}`;
    }
  } catch {
    // ignore and fall back
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getSessionId(config?: SessionConfig): string | null {
  if (config && config.enabled === false) return null;
  const key = config?.key || DEFAULT_SESSION_KEY;
  const ttlMinutes = config?.ttlMinutes ?? DEFAULT_TTL_MINUTES;
  const ttlMs = Math.max(1, ttlMinutes) * 60 * 1000;
  const storage = getStorage(config?.storage);
  if (!storage) return generateSessionId();

  try {
    const raw = storage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; expiresAt?: number };
      if (parsed?.id && parsed?.expiresAt && parsed.expiresAt > Date.now()) {
        return parsed.id;
      }
    }
  } catch {
    // ignore storage parse issues
  }

  const id = generateSessionId();
  const expiresAt = Date.now() + ttlMs;
  try {
    storage.setItem(key, JSON.stringify({ id, expiresAt }));
    // Backwards compatibility for any backend expecting session_id
    storage.setItem('session_id', id);
  } catch {
    // ignore storage errors
  }
  return id;
}

export function clearSessionId(config?: SessionConfig) {
  const key = config?.key || DEFAULT_SESSION_KEY;
  const storage = getStorage(config?.storage);
  if (!storage) return;
  try {
    storage.removeItem(key);
    storage.removeItem('session_id');
  } catch {
    // ignore
  }
}
